import { FC, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { ReviewSentimentAnalysesSection } from 'src/charts/ReviewSentimentAnalysesSection'
import { BackNavigationButton } from 'src/components/BackNavigationButton'
import { REVIEW_ANALYSIS_CAMPAIGN_ROUTE } from 'src/constants/routes'
import { PLATFORM_ORDER } from 'src/pages/ReviewAnalysis/constants'
import { useGetReviewAnalysisPlatformAnalysisQuery } from 'src/reactQuery/useGetReviewAnalysisPlatformAnalysisQuery'
import {
  useReviewAnalysisDateRange,
  useReviewAnalysisLinkQuery,
} from 'src/hooks/useReviewAnalysisDateRange'
import { createLink } from 'src/utils/createLink'
import { GuestReviewsOverview } from './GuestReviewsOverview'
import { PlatformReviewAnalysis } from './PlatformReviewAnalysis'
import { ReviewSmartInsights } from '../ReviewSmartInsights'
import { ReviewTopicsAnalysis } from '../BrandLevelWidgets/ReviewTopicsAnalysis'
import { TopReviewTopics } from '../FacilityLevelWidgets/TopReviewTopics'
import classes from '../BrandLevelWidgets/brandLevelWidgets.module.scss'

export const PlatformLevelWidgets: FC = () => {
  const { campaignId = '', platformId = '' } = useParams<{
    campaignId: string
    platformId: string
  }>()

  // The header's "All Facilities" filter ([] = all). Reset when the platform
  // changes: a facility on one platform may not exist on another.
  const [facilityIds, setFacilityIds] = useState<string[]>([])
  useEffect(() => {
    setFacilityIds([])
  }, [platformId])

  // Shared with PlatformReviewAnalysis (react-query dedupes), just to read the
  // platform name for the back-navigation title.
  const dateRange = useReviewAnalysisDateRange()
  const linkQuery = useReviewAnalysisLinkQuery()
  const { data } = useGetReviewAnalysisPlatformAnalysisQuery(
    campaignId,
    platformId,
    undefined,
    dateRange
  )

  return (
    <div className={classes.wrapper}>
      <div className={classes.backNav}>
        <BackNavigationButton
          className={classes.backNavButton}
          title={data?.platformName ?? ''}
          url={createLink(REVIEW_ANALYSIS_CAMPAIGN_ROUTE, {
            params: { campaignId },
            queryParams: linkQuery,
          })}
        />
      </div>
      <ReviewSmartInsights platformId={platformId} />
      <PlatformReviewAnalysis
        facilityIds={facilityIds}
        onFacilitiesChange={setFacilityIds}
      />
      {/* Topics & Subtopics table (with Emerging Topics), scoped to this
          platform across all facilities — as on the facility page. */}
      <div className={classes.topicsAnalysis}>
        <ReviewTopicsAnalysis
          campaignId={campaignId}
          platform={platformId === 'all' ? undefined : platformId}
        />
      </div>
      {/* Hidden until the platform's reviews carry sub-ratings. */}
      <GuestReviewsOverview facilityTabs={data?.facilityTabs} />
      <TopReviewTopics level="platform" variant="positive" />
      <TopReviewTopics level="platform" variant="negative" />
      {/* After the page's own read, as when it sat inside the analysis
          section: no jump on first load, and an unknown platform in the URL
          (a 404) shows no unfiltered list. */}
      {data && (
        <div className={classes.sentimentSection}>
          <ReviewSentimentAnalysesSection
            campaignId={campaignId}
            facilityIds={facilityIds}
            platform={PLATFORM_ORDER.find((p) => p === data.platform)}
          />
        </div>
      )}
    </div>
  )
}
